// Lightweight microsurvey modal — fires when a user dismisses the upgrade
// modal 3 times. Captures one quick-pick reason + optional free text so
// silent churn becomes actionable signal on /admin.
import { useState } from "react";
import { X, Send } from "lucide-react";
import { submitMicrosurvey } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
    "Too expensive",
    "Don't need the features yet",
    "Just exploring",
    "Found something elsewhere",
    "Not sure what I get",
    "Other (write below)",
];

export default function Microsurvey({ trigger, onClose }) {
    const [reason, setReason] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        if (!reason && !message.trim()) {
            toast.error("Pick a reason or write a message");
            return;
        }
        setSending(true);
        try {
            await submitMicrosurvey({
                trigger,
                reason_tag: reason,
                message: message.trim(),
                page_url: window.location.pathname + window.location.search,
            });
            toast.success("Thanks for the feedback");
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[90] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="microsurvey-modal"
        >
            <div className="bl-card max-w-md w-full overflow-hidden relative shadow-2xl">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-3 right-3 p-1.5 rounded-full text-bl-muted hover:text-bl"
                    data-testid="microsurvey-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="px-6 pt-6 pb-3">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-semibold">
                        Quick question
                    </div>
                    <h3 className="mt-1 font-display font-semibold text-xl text-bl">
                        What's stopping you from upgrading?
                    </h3>
                    <p className="mt-1 text-xs text-bl-muted">
                        Two clicks. We read every response.
                    </p>
                </div>
                <div className="px-6 pb-4 space-y-2">
                    {REASONS.map((r) => (
                        <label
                            key={r}
                            className={
                                "flex items-center gap-2 p-2 rounded border cursor-pointer text-sm "
                                + (reason === r
                                    ? "border-bl-primary bg-bl-teal-tint text-bl"
                                    : "border-bl text-bl-muted hover:bg-bl-bg")
                            }
                            data-testid={`microsurvey-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <input
                                type="radio"
                                name="reason"
                                value={r}
                                checked={reason === r}
                                onChange={() => setReason(r)}
                                className="accent-bl-primary"
                            />
                            {r}
                        </label>
                    ))}
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                        placeholder="Optional: anything else? (500 chars)"
                        rows={2}
                        className="w-full mt-2 px-3 py-2 border border-bl rounded bg-bl-surface text-bl text-sm"
                        data-testid="microsurvey-message"
                    />
                </div>
                <div className="px-6 pb-5 flex gap-2 justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bl-btn-ghost text-sm"
                        data-testid="microsurvey-skip"
                    >
                        Skip
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={sending}
                        className="bl-btn-primary text-sm inline-flex items-center gap-1.5"
                        data-testid="microsurvey-submit"
                    >
                        <Send className="w-3.5 h-3.5" />
                        {sending ? "Sending…" : "Send feedback"}
                    </button>
                </div>
            </div>
        </div>
    );
}
